<template>
    <div class="">
        <dean-notification></dean-notification>
        <grid-layout
                :layout="processLayout"
                :autoSize="true"
                :col-num="calculateColumnNum()"
                :row-height="grid.rowHeight"
                :is-draggable="true"
                :is-resizable="false"
                :is-mirrored="false"
                :vertical-compact="true"
                :use-css-transforms="true"
                :margin="grid.margin"
                @layout-updated="layoutUpdatedEvent"
        >
            <grid-item v-for="item in processLayout"
                       :x="item.x"
                       :y="item.y"
                       :w="item.w"
                       :h="item.h"
                       :i="item.i"
                       :key="item.i"
                       dragIgnoreFrom="a, button, .dean-dialog-slot, .dean-dialog-button-group">
                <div class="container">
                    <component v-bind:is="item.widget"></component>
                </div>
            </grid-item>
        </grid-layout>
    </div>
</template>

<script lang="ts">

  import {Component, Vue} from 'vue-property-decorator';
  import VueGridLayout from 'vue-grid-layout';
  import axios from 'axios';

  // Widgets
  import {saveIcon, warningIcon} from '@deanone/dean-components';
  import DeanNotification from '@/components/deanComponents/deanNotification.vue';
  import NewOfferWidget from '@/components/widgets/NewOfferWidget.vue';
  import NewCustomersWidget from '@/components/widgets/NewCustomersWidget.vue';
  import PostCodeCheckerWidget from '@/components/widgets/PostcodeCheckerWidget.vue';
  import NumberCheckerWidget from '@/components/widgets/NumberCheckerWidget.vue';
  import OrderSimCardsWidget from '@/components/widgets/OrderSimCardsWidget.vue';
  import TopCustomersWidget from '@/components/widgets/TopCustomersWidget.vue';
  import CurrentMonthOffers from '@/components/widgets/CurrentMonthOffers.vue';
  import MonthlyTurnover from '@/components/widgets/MonthlyTurnover.vue';
  import MonthlyOrders from '@/components/widgets/MonthlyOrders.vue';
  import MonthlyUsers from '@/components/widgets/MonthlyUsers.vue';
  import CustomerGrowthWidget from '@/components/widgets/CustomerGrowthWidget.vue';
  import MonthlyDiscountDevelopment from '@/components/widgets/MonthlyDiscountDevelopment.vue';
  import MobileOrdersGraph from '@/components/widgets/MobileOrdersGraph.vue';
  import MobileOffersGraph from '@/components/widgets/MobileOffersGraph.vue';
  import TelephoneUsersGraph from '@/components/widgets/TelephoneUsersGraph.vue';
  import TurnoverPerMonthGraph from '@/components/widgets/TurnoverPerMonthGraph.vue';
  import MonthlyOrdersGraph from '@/components/widgets/MonthlyOrdersGraph.vue';
  import CurrentMonthOffersValue from '@/components/widgets/CurrentMonthOffersValue.vue';
  import WinLostGraph from '@/components/widgets/WinLostGraph.vue';
  import TopQuotesWidget from '@/components/widgets/TopQuotesWidget.vue';
  import MonthlyMobileTurnover from '@/components/widgets/MonthlyMobileTurnover.vue';
  import GammaKnowledgeBaseWidget from '@/components/widgets/GammaKnowledgeBaseWidget.vue';
  import GammaPartnerHubWidget from '@/components/widgets/GammaPartnerHubWidget.vue';
  import { Version } from '@/plugins/ovVersion';

  @Component({
    components: {
      DeanNotification,
      NewOfferWidget,
      NewCustomersWidget,
      PostCodeCheckerWidget,
      NumberCheckerWidget,
      OrderSimCardsWidget,
      TopCustomersWidget,
      CurrentMonthOffers,
      MonthlyTurnover,
      MonthlyOrders,
      MonthlyUsers,
      MobileOrdersGraph,
      MobileOffersGraph,
      MonthlyDiscountDevelopment,
      CustomerGrowthWidget,
      TelephoneUsersGraph,
      TurnoverPerMonthGraph,
      MonthlyOrdersGraph,
      CurrentMonthOffersValue,
      WinLostGraph,
      TopQuotesWidget,
      MonthlyMobileTurnover,
      GammaKnowledgeBaseWidget,
      GammaPartnerHubWidget,
      GridLayout: VueGridLayout.GridLayout,
      GridItem: VueGridLayout.GridItem,
    },
  })

  export default class OneviewDashboard extends Vue {
    public BASE_LEN: number = 220;
    public MARGIN: number = 10;

    public window: { width: number, height: number } = {
      width: 0,
      height: 0,
    };
    public grid: { rowHeight: number, margin: [number, number] } = {
      rowHeight: this.BASE_LEN,
      margin: [this.MARGIN, this.MARGIN],
    };

    public layouts: { [key: number]: object|null } = {};
    public widgets = [
      {
        widget: 'WinLostGraph',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'NewOfferWidget',
        width: 1,
        height: 1,
        privileges: ['create-quote'],
      },
      {
        widget: 'NewCustomersWidget',
        width: 1,
        height: 1,
        privileges: [],
      },
      {
        widget: 'PostCodeCheckerWidget',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'NumberCheckerWidget',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'OrderSimCardsWidget',
        width: 3,
        height: 3,
        privileges: [],
      },
      {
        widget: 'GammaKnowledgeBaseWidget',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'GammaPartnerHubWidget',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'CurrentMonthOffers',
        width: 1,
        height: 1,
        privileges: [],
      },
      {
        widget: 'MonthlyTurnover',
        width: 1,
        height: 1,
        privileges: [],
      },
      {
        widget: 'MonthlyOrders',
        width: 1,
        height: 1,
        privileges: [],
      },
      {
        widget: 'MonthlyUsers',
        width: 1,
        height: 1,
        privileges: [],
      },
      {
        widget: 'CustomerGrowthWidget',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'MonthlyDiscountDevelopment',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'MobileOrdersGraph',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'MobileOffersGraph',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'TelephoneUsersGraph',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'TurnoverPerMonthGraph',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'MonthlyOrdersGraph',
        width: 2,
        height: 2,
        privileges: [],
      },
      {
        widget: 'CurrentMonthOffersValue',
        width: 1,
        height: 1,
        privileges: [],
      },
      {
        widget: 'TopCustomersWidget',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'TopQuotesWidget',
        width: 2,
        height: 1,
        privileges: [],
      },
      {
        widget: 'MonthlyMobileTurnover',
        width: 1,
        height: 1,
        privileges: [],
      },
    ];

    public created() {
      this.handleResize();
      window.addEventListener('resize', this.handleResize);

      const selfthis = this;
      axios
        .get('/-/trade/api/dashboardlayout.info')
        .then(
          (result) => {
            // tslint:disable-next-line:no-console
            console.log('trade-service: ' + result.data.version);
            // tslint:disable-next-line:no-console
            console.log('ov-ui: ' + Version());
            if (result.data.layout) {
              Vue.set(selfthis.layouts, this.calculateColumnNum(), result.data.layout);
            }
          },
          (error) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
          },
        )
        .finally(
          () => {
            this.$store.dispatch('setLoadOverlay', false);
          },
        );
    }

    public destroyed() {
      window.removeEventListener('resize', this.handleResize);
    }

    public calculateColumnNum() {
      return Math.floor(this.window.width / this.BASE_LEN);
    }

    public handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    }

    public layoutUpdatedEvent(newLayout: object) {
      this.layouts[this.calculateColumnNum()] = newLayout;
      if (localStorage.layouts === JSON.stringify(this.layouts)) {
        return;
      }
      localStorage.layouts = JSON.stringify(this.layouts);

      axios
        .post('/-/trade/api/dashboardlayout.store', {layout: localStorage.layouts})
        .then((result) => {
            this.$toast({
              icon: saveIcon,
              text: 'Layout opgeslagen',
              theme: 'success',
              title: 'Opgeslagen',
            });
          },
          (error) => {
            this.$toast({
              icon: warningIcon,
              text: 'Er is iets mis gegaan, probeer het later nog eens.',
              theme: 'danger',
              title: 'Fout',
            });
          },
        );
    }

    get processLayout() {
      const columnNum = this.calculateColumnNum();
      let maxHeight = 0;

      // load saved layout if available
      if (localStorage.layouts) {
        this.layouts = JSON.parse(localStorage.layouts);
      }

      // is no layout for current screen size, generate one
      if (!this.layouts.hasOwnProperty(columnNum) || this.layouts[columnNum] === null) {
        const layout = [];
        let y = 0;
        let x = 0;
        let index = 0;
        for (const widget of this.widgets) {
          const width = widget.width;
          const height = widget.height;
          const privileges = widget.privileges;

          const intersect = (a: any, b: any) => {
            if (!a || !b) {
              return [];
            } else {
              let t;
              if (b.length > a.length) {
                t = b;
                b = a;
                a = t;
              }
              return a.filter((e: any) => {
                return b.indexOf(e) > -1;
              });
            }
          };

          let showWidget = false;

          if (privileges.length === 0) {
            showWidget = true;
          } else {
            const test = intersect(this.$store.getters.getUser.privileges, privileges);
            if (test.length > 0) {
              showWidget = true;
            }
          }

          if (showWidget === true) {
            layout.push({
              i: index,
              x,
              y,
              w: width,
              h: height,
              width: this.BASE_LEN * width - this.MARGIN * width,
              height: this.BASE_LEN * height - this.MARGIN * height,
              widget: widget.widget,
            });

            index = index + 1;
            if (height > maxHeight) {
              maxHeight = height;
            }
            x = x + width;
            if (x >= columnNum - 1) {
              x = 0;
              y = y + maxHeight;
              maxHeight = 0;
            }
          }
        }

        Vue.set(this.layouts, columnNum, layout);
      }

      return (this.layouts as any)[columnNum];
    }

  }
</script>

<style lang="scss" scoped>

    .margin-right {
        margin-right: 17px
    }

    .container {
        width: 100%;
        height: 100%;
    }

    .dean-tile {
        width: 100%;
        height: 100%;
        perspective: 0px;
        background-color: #FFFFFF;
        border-radius: 1.88rem;
        box-shadow: 0 0 0.38rem rgba(0, 0, 0, 0.2);
    }

    .grid-layout-container {
        display: inline-block;
        /*width: 390px;*/
        width: 100%;
        height: 100%;
    }

    .calendar {
        width: 100%;
        height: 100%;
        position: absolute;
        text-align: center;
        font-family: "Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif;
        color: #fff;
    }

    .calendar .calendar-week-day {
        margin-top: 20px;
        font-size: 14px;
        font-weight: 500;
    }

    .calendar .calendar-month-day {
        margin-top: 15px;
        font-size: 30px;
        transform: scale(1.75);
    }

    :deep(.dean-dialog-bar) {
      cursor: move;
      cursor: -moz-grab;
      cursor: -webkit-grab;
    }

</style>

<style lang="scss">
    .dashboard {
        text-align: center;
    }
</style>
